import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Checkbox, Container, FormHelperText, Link, TextField, Typography } from '@material-ui/core';

import { useDispatch } from 'react-redux';
import { setProfile } from '../store/actions/profile';
import { getSubscriptionInfoAction } from '../store/actions/subscription';

import api from '../api';

import { LoadingButton } from '../components/common';
import { useAlert } from '../hooks';
import { getErrorText } from '../utils';

const initialValues = {
	email: '',
	password: '',
	confirmpassword: '',
	policy: false,
	recaptcha: '',
};

const validationSchema = Yup.object().shape({
	email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
	password: Yup.string().max(255).required('password is required'),
	confirmpassword: Yup.string()
		.max(255)
		.required('password is required')
		.oneOf([Yup.ref('password'), null], 'Passwords must match'),
	policy: Yup.boolean().oneOf([true], 'This field must be checked'),
	recaptcha: Yup.string().required(),
});

export default function RegisterForm() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const callAlert = useAlert();

	const onSubmit = async (data, formikContext) => {
		const { resetForm } = formikContext;
		const { email } = data;
		try {
			const { data: resData } = await api.signup(data);
			if (resData.status === 'ok') {
				const { token, user } = resData;
				localStorage.token = token;
				resetForm();
				dispatch(setProfile({...user, name: email }));
				dispatch(getSubscriptionInfoAction());
				navigate('/admin/pricing', { replace: true });
			} else {
				if (resData.message === 'register.login.message-already-registered') {
					callAlert(getErrorText(resData?.message), 'info');
					navigate('/login', { replace: true });
				} else {
					callAlert(getErrorText(resData?.message), 'error');
				}
				console.error(resData);
			}
		} catch (error) {
			console.error(error);
			callAlert('An error has occurred. Please contact me by email.', 'error');
		}
	};

	return (
		<>
			<Helmet>
				<title>Register | Parcels API</title>
			</Helmet>
			<Box
				sx={{
					backgroundColor: 'background.default',
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					justifyContent: 'center',
					minHeight: '100vh',
				}}
			>
				<Container maxWidth="sm">
					<Formik {...{ initialValues, validationSchema, onSubmit }}>
						{({
							errors,
							handleBlur,
							handleChange,
							handleSubmit,
							isSubmitting,
							touched,
							values,
							setFieldValue,
						}) => (
							<form onSubmit={handleSubmit}>
								<Box sx={{ mb: 3 }}>
									<Typography color="textPrimary" align="center" variant="h2">
										Create new account
									</Typography>
									<Typography
										color="textSecondary"
										align="center"
										gutterBottom
										variant="body2"
										style={{ marginTop: 15 }}
									>
										Create your Parcels App API account
									</Typography>
								</Box>
								<TextField
									error={Boolean(touched.email && errors.email)}
									fullWidth
									helperText={touched.email && errors.email}
									label="Email Address"
									margin="normal"
									name="email"
									onBlur={handleBlur}
									onChange={handleChange}
									type="email"
									value={values.email}
									variant="outlined"
								/>
								<TextField
									error={Boolean(touched.password && errors.password)}
									fullWidth
									helperText={touched.password && errors.password}
									label="Password"
									margin="normal"
									name="password"
									onBlur={handleBlur}
									onChange={handleChange}
									type="password"
									value={values.password}
									variant="outlined"
								/>
								<TextField
									error={Boolean(touched.confirmpassword && errors.confirmpassword)}
									fullWidth
									helperText={touched.confirmpassword && errors.confirmpassword}
									label="Confirm password"
									margin="normal"
									name="confirmpassword"
									onBlur={handleBlur}
									onChange={handleChange}
									type="password"
									value={values.confirmpassword}
									variant="outlined"
								/>
								<Box
									sx={{
										alignItems: 'center',
										justifyContent: 'center',
										display: 'flex',
										ml: -1,
									}}
								>
									<Checkbox checked={values.policy} name="policy" onChange={handleChange} />
									<Typography color="textSecondary" variant="body1" align="center">
										I have read the
									</Typography>
									<Link
										target="_blank"
										href="https://parcelsapp.com/terms-api"
										rel="noreferrer"
										color="primary"
										underline="always"
										variant="h5"
										style={{ marginLeft: 6 }}
									>
										API Terms of Use
									</Link>
								</Box>
								{Boolean(touched.policy && errors.policy) && (
									<FormHelperText error>{errors.policy}</FormHelperText>
								)}
								<Box
									sx={{
										py: 2,
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
									}}
								>
									<ReCAPTCHA
										className="g-recaptcha"
										sitekey={process.env.REACT_APP_RECAPTCHA_KEY || ''}
										onChange={(response) => setFieldValue('recaptcha', response)}
									/>
									<LoadingButton
										style={{ marginTop: '20px' }}
										color="primary"
										disabled={
											isSubmitting ||
											Object.keys(errors)?.length > 0 ||
											Object.values(values).some((i) => !i)
										}
										fullWidth
										size="large"
										type="submit"
										variant="contained"
										loading={isSubmitting}
									>
										Sign up now
									</LoadingButton>
								</Box>
								<Typography color="textSecondary" variant="body1" align="center">
									Have an account?{' '}
									<Link component={RouterLink} to="/login" variant="h6">
										Sign in
									</Link>
								</Typography>
							</form>
						)}
					</Formik>
				</Container>
			</Box>
		</>
	);
}
